import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Heart, Award, Users, Clock } from 'lucide-react';
import SEO from '../components/SEO';

const About = () => {
  return (
    <div className="min-h-screen">
      <SEO 
        title="Over Ons"
        description="Leer ons team kennen. Wij zijn betrokken zorgverleners met ruime ervaring in de gehandicaptenzorg, gespecialiseerd in jeugdzorg."
        keywords="over ons, team, verpleegkundige, ervaring, gehandicaptenzorg"
        
      />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 
              className="text-5xl md:text-6xl font-bold text-[#1A4D4D] mb-6"
              style={{ fontFamily: 'Fredoka, Quicksand, sans-serif' }}
            >
              Over Ons
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Wij zijn betrokken en deskundige zorgverleners met een gedeelde passie voor de zorg aan kinderen en jongeren.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_skole-clone/artifacts/ojqi65oe_Image.jpeg"
                alt="KS Care Team"
                className="rounded-3xl shadow-2xl w-full"
              />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-[#1A4D4D] mb-6" style={{ fontFamily: 'Fredoka, Quicksand, sans-serif' }}>
                Onze Achtergrond
              </h2>
              <div className="space-y-4 text-gray-600 text-lg leading-relaxed">
                <p>
                  <strong>Ik ben (kinder)verpleegkundige</strong>, gespecialiseerd in de gehandicaptenzorg, en werk met veel liefde en toewijding binnen dit mooie werkveld.
                </p>
                <p>
                  Met ruime ervaring in het ondersteunen van kinderen en jongeren met een beperking, bied ik professionele zorg op maat. Mijn expertise ligt in het begeleiden van jeugd met moeilijk verstaanbaar gedrag (MVG).
                </p>
                <p>
                  Ik geloof dat ieder kind het recht heeft op een plek waar het <strong>gezien, gehoord en begrepen</strong> wordt.
                </p>
              </div>
            </div>
          </div>

          {/* Values Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            <div className="bg-gradient-to-br from-[#FFE8E0] to-white p-8 rounded-3xl shadow-lg text-center">
              <div className="w-16 h-16 bg-[#FF6347] rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#1A4D4D] mb-2">Met Hart & Ziel</h3>
              <p className="text-gray-600">Warme, persoonlijke benadering met oprechte betrokkenheid</p>
            </div>

            <div className="bg-gradient-to-br from-[#E0F2F7] to-white p-8 rounded-3xl shadow-lg text-center">
              <div className="w-16 h-16 bg-[#5DADE2] rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#1A4D4D] mb-2">Professioneel</h3>
              <p className="text-gray-600">Gediplomeerd en ervaren in gespecialiseerde jeugdzorg</p>
            </div>

            <div className="bg-gradient-to-br from-[#FFF4E0] to-white p-8 rounded-3xl shadow-lg text-center">
              <div className="w-16 h-16 bg-[#F39C12] rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#1A4D4D] mb-2">Kleinschalig</h3>
              <p className="text-gray-600">1-op-1 begeleiding in huiselijke, overzichtelijke omgeving</p>
            </div>

            <div className="bg-gradient-to-br from-[#E8F5E9] to-white p-8 rounded-3xl shadow-lg text-center">
              <div className="w-16 h-16 bg-[#52BE80] rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#1A4D4D] mb-2">Flexibel</h3>
              <p className="text-gray-600">Dagopvang én logeeropvang, afgestemd op uw behoefte</p>
            </div>
          </div>

          {/* Philosophy */}
          <div className="max-w-4xl mx-auto bg-gray-50 rounded-3xl p-12 mb-16">
            <h2 className="text-3xl font-bold text-[#1A4D4D] mb-6 text-center" style={{ fontFamily: 'Fredoka, Quicksand, sans-serif' }}>
              Onze Visie
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed text-center mb-6">
              Een plek waar ruimte is voor <strong>rust, structuur, ontwikkeling en plezier</strong> — én waar kinderen volledig zichzelf mogen zijn.
            </p>
            <p className="text-gray-600 text-lg leading-relaxed text-center">
              Onze kracht ligt in onze warme en huiselijke benadering, gecombineerd met duidelijke structuur, professionele expertise en intensieve begeleiding in kleine groepen (1 op 1). Hierdoor kunnen wij elke jeugdige op een veilige, respectvolle en persoonlijke manier ondersteunen.
            </p>
          </div>

          {/* CTA */}
          <div className="text-center">
            <h3 className="text-2xl font-bold text-[#1A4D4D] mb-4">Kom kennismaken!</h3>
            <p className="text-gray-600 mb-6">We nodigen u graag uit voor een vrijblijvende kennismaking.</p>
            <a href="mailto:info@kscare.nl?subject=Intake%20aanvraag">
              <Button className="bg-[#FF6347] hover:bg-[#FF5533] text-white rounded-full px-8 py-6 text-lg">
                Plan een intake
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;